// Kho lưu cho SaveStore: IndexedDB (chính) và localStorage (bản sao). Bản bộ nhớ dùng cho test.
import { createStore, get, set, del, keys, type UseStore } from 'idb-keyval';

export interface KvBackend {
  get(key: string): Promise<string | undefined>;
  set(key: string, value: string): Promise<void>;
  del(key: string): Promise<void>;
  keys(): Promise<string[]>;
}

export interface MirrorBackend {
  read(): string | null;
  /** false nếu không ghi được (hết quota, bị chặn). */
  write(value: string): boolean;
  clear(): void;
}

export const IDB_NAME = 'hoi-tan';
export const IDB_STORE = 'save';
export const MIRROR_KEY = 'ht-save-mirror';

/**
 * Tên kho save theo đường gốc của bản build. Site ghép nhiều bản trên cùng origin (`/` live, `/beta/`,
 * `/beta/mX/`); dùng chung kho thì bản beta migrate save lên phiên bản mới và bản live không đọc được nữa.
 * Bản ở gốc giữ đúng tên cũ (save hiện có không phải chuyển).
 */
export function saveNamespace(base: string): { idbName: string; mirrorKey: string } {
  const tag = base.replace(/^\/+|\/+$/g, '');
  if (!tag) return { idbName: IDB_NAME, mirrorKey: MIRROR_KEY };
  return { idbName: `${IDB_NAME}@${tag}`, mirrorKey: `${MIRROR_KEY}@${tag}` };
}

export function idbKv(name: string = IDB_NAME): KvBackend {
  let store: UseStore | null = null;
  const s = (): UseStore => (store ??= createStore(name, IDB_STORE));
  return {
    get: async (k) => {
      const v: unknown = await get(k, s());
      return typeof v === 'string' ? v : undefined;
    },
    set: (k, v) => set(k, v, s()),
    del: (k) => del(k, s()),
    keys: async () => (await keys(s())).map(String),
  };
}

export function localStorageMirror(storage: Storage = localStorage, key: string = MIRROR_KEY): MirrorBackend {
  return {
    read: () => {
      try {
        return storage.getItem(key);
      } catch {
        return null;
      }
    },
    write: (v) => {
      try {
        storage.setItem(key, v);
        return true;
      } catch {
        return false;
      }
    },
    clear: () => {
      try {
        storage.removeItem(key);
      } catch {
        /* bỏ qua: mirror chỉ là bản sao */
      }
    },
  };
}

export function memoryKv(init: Record<string, string> = {}): KvBackend & { data: Map<string, string> } {
  const data = new Map(Object.entries(init));
  return {
    data,
    get: async (k) => data.get(k),
    set: async (k, v) => void data.set(k, v),
    del: async (k) => void data.delete(k),
    keys: async () => [...data.keys()],
  };
}

export function memoryMirror(init: string | null = null): MirrorBackend & { value: string | null } {
  const m = {
    value: init,
    read: () => m.value,
    write: (v: string) => ((m.value = v), true),
    clear: () => void (m.value = null),
  };
  return m;
}
